import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getContractTypeLabel } from '../../../../constants/labels/user';
import { useUpdateUserMutation } from '../../../../hooks/mutations/useUserMutation';
import { useJobsQuery } from '../../../../hooks/queries/useJobsQuery';
import { useProfilesQuery } from '../../../../hooks/queries/useProfilesQuery';
import { useUsersQuery } from '../../../../hooks/queries/useUsersQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { User } from '../../../../services/apiSLTP/users';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { getStatusColor } from '../../../../utils/colors';
import {
    crudStatus,
    getCrudStatusInfos,
    getSpecialUserTypeLabel,
    localStorageKeys,
    permissionLevels,
    systemConfigKeyEnum,
} from '../../../../utils/enums';
import { formatNumber } from '../../../../utils/numbers';
import StateCell from '../../../cells/StateCell';
import IconLine from '../../../IconLine';
import { InheritedPriceDisplay } from '../../../InheritedPriceDisplay';
import UpdateUserModal from '../../../modals/admin/internal/UpdateUserModal';
import UpdateUserPasswordModal from '../../../modals/admin/internal/UpdateUserPasswordModal';
import UpdateUserSecurityPassModal from '../../../modals/admin/internal/UpdateUserSecurityPassModal';
import DeleteModal from '../../../modals/DeleteModal';
import IconButton from '../../../shared/IconButton';
import GenericAgGridTable from '../../GenericAgGridTable';
import { createDateColumn, createDateTimeColumn } from '../../../../utils/dates';

interface UserSettingsRowProps {
    withUsageCount: boolean;
}

const UserSettingsRow: React.FC<UserSettingsRowProps> = ({ withUsageCount }) => {
    const [showEditModal, setShowEditModal] = useState(false);
    const [showSecurityPassModal, setShowSecurityPassModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const { t } = useTranslation();
    const { user } = useAuth();
    const { getConfigValue } = useSystemConfig();

    const { data: dataUsers, isLoading: usersIsLoading, refetch: refetchUsers } = useUsersQuery({ queries: { includeFiles: true } });

    const { data: dataJobs, isLoading: jobsIsLoading, refetch: refetchJobs } = useJobsQuery({ queries: { active: true } });
    const {
        data: dataProfiles,
        isLoading: profilesIsLoading,
        isError: profilesIsError,
        isSuccess: profilesIsSuccess,
        refetch: profilesRefetch,
    } = useProfilesQuery({});

    const {
        mutate: mutateUser,
        isSuccess: isSuccessUser,
        isError: isErrorUser,
        isLoading: isLoadingUser,
        error: errorUser,
        data: dataUser,
    } = useUpdateUserMutation({
        onSuccess: () => {
            refetchUsers();
        },
        onError: (error) => {
            console.error('Error updating user:', error.message);
        },
    });

    const sortedUsers = () => {
        return (
            dataUsers?.sort((a, b) => {
                const nameA = a?.lastName + ' ' + a?.firstName;
                const nameB = b?.lastName + ' ' + b?.firstName;
                if (nameA < nameB) return -1;
                if (nameA > nameB) return 1;
                return 0;
            }) || []
        );
    };

    const columnDefs: ColDef<User>[] = [
        {
            cellStyle: (params) => {
                const rowIndex = params.node.rowIndex;
                if (rowIndex === null) return null;
                const api = params.api;

                const prevRowNode = api.getDisplayedRowAtIndex(rowIndex - 1);
                const nextRowNode = api.getDisplayedRowAtIndex(rowIndex + 1);

                const prevId = prevRowNode?.data?._id;
                const nextId = nextRowNode?.data?._id;
                const currentId = params.data?._id;

                const marginTop = prevId !== currentId ? '5px' : '0';
                const marginBottom = nextId !== currentId ? '5px' : '0';

                const height = `calc(100% - ${parseInt(marginTop) + parseInt(marginBottom)}px)`;

                return {
                    padding: 0,
                    marginLeft: 5,
                    backgroundColor: params.data?.color || 'transparent',
                    marginTop,
                    marginBottom,
                    height,
                    borderBottomLeftRadius: nextId !== currentId ? 10 : 0,
                    borderBottomRightRadius: nextId !== currentId ? 10 : 0,
                    borderTopLeftRadius: prevId !== currentId ? 10 : 0,
                    borderTopRightRadius: prevId !== currentId ? 10 : 0,
                    width: 20,
                };
            },
            colId: 'color',
            resizable: false,
            filter: false,
            sortable: false,
            minWidth: 30,
            width: 30,
            valueGetter: (params) => params.data?.color || '',
            cellRenderer: () => '',
            enableRowGroup: false,
            suppressHeaderMenuButton: true,
        },
        {
            headerName: capitalizeFirstLetter(t('lastName')),
            field: 'lastName',
            initialFlex: 1,
            minWidth: 200,
            colId: 'lastName',
        },
        {
            headerName: capitalizeFirstLetter(t('firstName')),
            field: 'firstName',
            initialFlex: 1,
            minWidth: 200,
            colId: 'firstName',
        },
        { headerName: capitalizeFirstLetter(t('codeProC')), field: 'codeProC', colId: 'codeProC', initialWidth: 150 },

        {
            headerName: capitalizeFirstLetter(t('actions')),
            cellRenderer: (params: ICellRendererParams<User>) => {
                if (!params.data) return null;
                return (
                    <IconLine>
                        <IconButton
                            iconName="edit"
                            title={capitalizeFirstLetter(t('modifyUser'))}
                            onClick={() => {
                                if (!params.data) return;
                                setSelectedUser(params.data);
                                setIsEditMode(true);
                                setShowEditModal(true);
                            }}
                            disabled={params.data?.crudStatus === crudStatus.DELETED}
                        />
                        <IconButton
                            iconName="badge"
                            title={capitalizeFirstLetter(t('modifySecurityPassport'))}
                            onClick={() => {
                                if (!params.data) return;
                                setSelectedUser(params.data);
                                setShowSecurityPassModal(true);
                            }}
                            disabled={params.data?.crudStatus === crudStatus.DELETED}
                        />
                        {user && user.profileId.permsLevel <= permissionLevels.ADMIN && (
                            <>
                                <IconButton
                                    iconName="lockReset"
                                    title={capitalizeFirstLetter(t('modifyPassword'))}
                                    onClick={() => {
                                        if (!params.data) return;
                                        setSelectedUser(params.data);
                                        setShowPasswordModal(true);
                                    }}
                                    disabled={params.data?.crudStatus === crudStatus.DELETED}
                                />
                                <IconButton
                                    iconName="delete"
                                    title={capitalizeFirstLetter(t('deleteUser'))}
                                    onClick={() => {
                                        if (!params.data) return;
                                        setSelectedUser(params.data);
                                        setShowDeleteModal(true);
                                    }}
                                    disabled={params.data?.crudStatus === crudStatus.DELETED}
                                />
                            </>
                        )}
                    </IconLine>
                );
            },
            colId: 'actions',
            filter: false,
            enableRowGroup: false,
            enableValue: false,
            sortable: false,
        },
        {
            headerName: capitalizeFirstLetter(t('status')),
            valueGetter: (params) => capitalizeFirstLetter(t(getCrudStatusInfos(params.data?.crudStatus))),
            cellRenderer: (params: ICellRendererParams<User>) => {
                if (!params.data) return params.value;
                return <StateCell stateColor={getStatusColor(params.data?.crudStatus)} text={t(getCrudStatusInfos(params.data?.crudStatus))} />;
            },
            colId: 'status',
        },
        { headerName: capitalizeFirstLetter(t('address')), field: 'address', colId: 'address', initialWidth: 300 },
        {
            headerName: capitalizeFirstLetter(t('latitude')),
            field: 'latitude',
            colId: 'latitude',
            valueFormatter: (params) => formatNumber(params.value, t('locale'), 8),
            type: 'numericColumn',
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: capitalizeFirstLetter(t('longitude')),
            field: 'longitude',
            colId: 'longitude',
            valueFormatter: (params) => formatNumber(params.value, t('locale'), 8),
            type: 'numericColumn',
            filter: 'agNumberColumnFilter',
        },
        { headerName: capitalizeFirstLetter(t('mobile')), field: 'phoneNumber', colId: 'mobile' },
        { headerName: capitalizeFirstLetter(t('username')), field: 'username', colId: 'username' },
        { headerName: capitalizeFirstLetter(t('job')), field: 'jobId.designation', colId: 'job', initialWidth: 250 },
        { headerName: capitalizeFirstLetter(t('profile')), field: 'profileId.type', colId: 'profile' },
        {
            headerName: capitalizeFirstLetter(t('contractType')),
            valueGetter: (params) => getContractTypeLabel(params.data?.contractType).toUpperCase(),
            colId: 'contractType',
        },
        {
            headerName: capitalizeFirstLetter(t('payrollNumber')),
            field: 'payrollNumber',
            colId: 'payrollNumber',
        },
        createDateColumn<User>('arrivalDate', 'arrivalDate'),
        createDateColumn<User>('departureDate', 'departureDate'),

        {
            headerName: `${capitalizeFirstLetter(t('price'))} (${getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)})`,
            type: 'numericColumn',
            filter: 'agNumberColumnFilter',
            valueGetter: (params) => params.data?.price || params.data?.jobId?.price,
            cellRenderer: (params: ICellRendererParams<User>) => {
                if (!params.data) return params.value;
                return (
                    <InheritedPriceDisplay
                        directValue={params.data.price}
                        inheritedValue={params.data.jobId?.price}
                        format={(v) => formatNumber(v, t('locale'))}
                    />
                );
            },
            colId: 'price',
        },
        /*{
            headerName: t('pricing'),
            valueGetter: (params) => capitalizeFirstLetter(t(getPriceTypeInfos(params.data?.priceType || params.data?.jobId?.priceType))),
            cellRenderer: (params: ICellRendererParams<User>) => {
                if (!params.data) return params.value;
                return (
                    <InheritedPriceDisplay
                        directValue={params.data.priceType}
                        inheritedValue={params.data.jobId?.priceType}
                        format={(v) => capitalizeFirstLetter(t(getPriceTypeInfos(v)))}
                    />
                );
            },
            colId: 'pricing',
        },*/
        {
            headerName: t('sst').toUpperCase(),
            field: 'isSST',
            colId: 'sst',
            valueFormatter: (params) => (params.value ? capitalizeFirstLetter(t('true')) : capitalizeFirstLetter(t('false'))),
        },
        {
            headerName: capitalizeFirstLetter(t('special')),
            field: 'isSpecial',
            colId: 'special',
            valueFormatter: (params) => (params.value ? capitalizeFirstLetter(t('true')) : capitalizeFirstLetter(t('false'))),
        },
        {
            headerName: capitalizeFirstLetter(t('specialUserType')),
            valueGetter: (params) => capitalizeFirstLetter(t(getSpecialUserTypeLabel(params.data?.specialType))),
            colId: 'specialUserType',
        },
        {
            headerName: capitalizeFirstLetter(t('securityPassport')),
            filter: false,
            sortable: false,
            cellRenderer: (params: ICellRendererParams<User>) => {
                if (!params.data) return null;
                return (
                    <>
                        {params.data.securityPassportFile?.url && (
                            <IconButton
                                iconName="pictureAsPdf"
                                onClick={() => window.open(params.data?.securityPassportFile?.url, '_blank')}
                                sx={{ color: '#B30B00' }}
                            />
                        )}
                    </>
                );
            },
            colId: 'passport',
        },
        createDateTimeColumn<User>('createdAt', 'createdAt'),
        createDateTimeColumn<User>('updatedAt', 'updatedAt'),
    ];

    if (user?.profileId.permsLevel === permissionLevels.ADMIN) {
        columnDefs.push({
            headerName: capitalizeFirstLetter(t('_id')),
            field: '_id',
            minWidth: 250,
            cellClass: 'usage-col-main',
            headerClass: 'usage-col-main',
            colId: '_id',
        });
    }

    return (
        <>
            <GenericAgGridTable
                rowData={sortedUsers()}
                columnDefs={columnDefs}
                loading={usersIsLoading}
                exportCsvFileName="admin-users"
                headerRightComponent={
                    <IconButton
                        iconName="addCircle"
                        onClick={() => {
                            setSelectedUser(null);
                            setIsEditMode(false);
                            setShowEditModal(true);
                        }}
                        title={capitalizeFirstLetter(t('addUser'))}
                    />
                }
                storageKey={localStorageKeys.TABLE.ADMIN.INTERNAL.USERS}
                agGridProps={{ getRowId: ({ data }) => data._id }}
            />
            {showEditModal && (
                <UpdateUserModal
                    showEditModal={showEditModal}
                    setShowEditModal={setShowEditModal}
                    user={selectedUser}
                    isEditMode={isEditMode}
                    refetch={() => refetchUsers()}
                    jobs={dataJobs || []}
                    profiles={dataProfiles || []}
                />
            )}
            {showSecurityPassModal && (
                <UpdateUserSecurityPassModal
                    showEditModal={showSecurityPassModal}
                    setShowEditModal={setShowSecurityPassModal}
                    user={selectedUser}
                    refetch={() => refetchUsers()}
                />
            )}
            {showPasswordModal && (
                <UpdateUserPasswordModal
                    showEditModal={showPasswordModal}
                    setShowEditModal={setShowPasswordModal}
                    user={selectedUser}
                    refetch={() => refetchUsers()}
                />
            )}
            {showDeleteModal && (
                <DeleteModal
                    open={showDeleteModal}
                    handleClose={() => setShowDeleteModal(false)}
                    deleteAction={() => {
                        if (selectedUser) {
                            mutateUser({ userId: selectedUser._id, data: { crudStatus: crudStatus.DELETED } });
                        }
                    }}
                    title={t('user')}
                    itemName={selectedUser?.firstName + ' ' + selectedUser?.lastName}
                />
            )}
        </>
    );
};

export default UserSettingsRow;
